import qs from 'qs';
import { useMemo } from 'react';

import { allIncludeOptionKeys } from '../../../../shared/reports';
import { useQuery } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import { STANDARD_REPORT_OPTIONS } from '../constants';
import type { DependencyInfoOption } from '../../../../shared/reports';
import type { ReportsV3QueryParams, ReportV3Format } from '../types';

import { useReportFlow } from './useReportFlow';

/**
 * The shape of params sent to the backend report API via `qs.stringify`.
 * This is distinct from `ReportsV3QueryParams` (URL state) — booleans are
 * real `boolean` values and `excludeFields` uses the nested format the
 * backend expects.
 */
type BackendReportParams = {
  [key: string]: boolean | DependencyInfoOption[] | { packageLabels: string[] } | undefined;
  dependencyInfoOptions?: DependencyInfoOption[];
  excludeFields?: { packageLabels: string[] };
};

/**
 * Returns format-specific include params for the standard report flow.
 */
const getFormatSpecificParams = (format?: ReportV3Format): Record<string, boolean> => {
  if (!format) {
    return {};
  }
  switch (format) {
    case 'TXT':
      return { includeFileMatches: true };
    case 'CYCLONEDX_JSON':
    case 'CYCLONEDX_XML':
      return { includeClosedVulnerabilities: true, includeSupportStatus: true };
    case 'HTML':
    case 'PDF':
    case 'CSV':
    case 'MD':
    case 'SPDX':
    case 'SPDX_JSON':
    case 'JSON':
      return {};
    default: {
      // If we get here, a new format was added to ReportV3Format
      // but not handled here. Add a case for it above.
      const exhaustiveCheck: never = format;
      return exhaustiveCheck;
    }
  }
};

/**
 * Returns a query string of report option params based on the current report
 * flow. For the standard flow this is a fixed set of include options plus
 * package metadata fields and format-specific params. For the custom flow it
 * reads the user's selections from URL query params.
 */
export const useReportOptionsParams = (): string => {
  const [flow] = useReportFlow();
  const [query] = useQuery<ReportsV3QueryParams>();
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(window?.user?.organizationId ?? 0);

  return useMemo(() => {
    if (flow === 'custom') {
      // Collect selected include booleans from URL params. Values are strings
      // because qs.parse returns strings; we convert to real booleans for the
      // backend query string.
      const includeParams = allIncludeOptionKeys
        .filter((key) => isLicenseConcludedEnabled || key !== 'includeConcludedLicenses')
        .reduce<Record<string, boolean>>((acc, key) => {
          if (query[key] === 'true') {
            acc[key] = true;
          }
          return acc;
        }, {});

      const dependencyInfoOptions = (query.dependencyInfoOptions ?? [])
        .filter((opt) => isLicenseConcludedEnabled || opt !== 'ConcludedLicense');
      const excludePackageLabels = query.excludePackageLabels ?? [];

      const params: BackendReportParams = {
        ...includeParams,
        ...(dependencyInfoOptions.length > 0 ? { dependencyInfoOptions } : {}),
        ...(excludePackageLabels.length > 0 ? { excludeFields: { packageLabels: excludePackageLabels } } : {}),
        ...getFormatSpecificParams(query.format),
      };

      return qs.stringify(params);
    }

    const includeParams = STANDARD_REPORT_OPTIONS
      .filter((option) => option.kind === 'includeBoolean')
      .filter((option) => isLicenseConcludedEnabled || option.id !== 'includeConcludedLicenses')
      .reduce<Record<string, boolean>>((acc, option) => {
        acc[option.id] = true;
        return acc;
      }, {});

    const dependencyInfoOptions = STANDARD_REPORT_OPTIONS
      .filter((option) => option.kind === 'dependencyInfoGroup')
      .flatMap((option) =>
        option.dependencyInfoOptions.filter((opt) => isLicenseConcludedEnabled || opt !== 'ConcludedLicense'),
      );

    const params: BackendReportParams = {
      ...includeParams,
      ...(dependencyInfoOptions.length > 0 ? { dependencyInfoOptions } : {}),
      ...getFormatSpecificParams(query.format),
    };

    return qs.stringify(params);
  }, [flow, query, isLicenseConcludedEnabled]);
};
